import { cloneElement, isValidElement, ReactNode } from 'react';
import { useSelector } from 'react-redux';
import parse from 'html-react-parser';

import { CallStatus } from '../../../../../../lib/enums/call-status.enum';
import { InboxMessage } from '../../../../../../lib/models/ams/types';
import { RootState } from '../../../../../../lib/store';
import { AMSCall } from '../../../../../../lib/store/ams/types';
import {
  extractDateFromString,
  formatUTCdateToLocalDate,
} from '../../../../../../lib/util/DateTimeHelpers';

import styles from '../AllNotifications.module.scss';

type Props = {
  messageId: number;
}

const NotificationInfo = ({ messageId } : Props) => {
  const currentMessage = useSelector<RootState, InboxMessage | null>((state: RootState) => state.ams.currentMessage);
  const isMessageReady = useSelector<RootState, boolean>(
    (state: RootState) => state.ams.statusByCall[AMSCall.getMessage]?.status === CallStatus.READY,
  );

  if (currentMessage?.id !== messageId) {
    return null;
  }

  const parsedBody = parse(currentMessage?.body ?? '');
  const nodes = (Array.isArray(parsedBody) ? parsedBody : [parsedBody]) as ReactNode[];
  const renderedBody = nodes.map((node, index) => {
    if (!isValidElement(node)) {
      return node;
    }

    const elementKey = node.key ?? `notification-body-${index}`;

    if (node.type === 'p') {
      const { children } = node.props;
      let textContent: string | undefined;
      if (Array.isArray(children)) {
        textContent = children.map((child) => (typeof child === 'string' ? child : '')).join('');
      } else if (typeof children === 'string') {
        textContent = children;
      }

      if (textContent) {
        const stringDate = extractDateFromString(textContent);
        if (stringDate) {
          const localizedDate = formatUTCdateToLocalDate(stringDate);
          if (localizedDate) {
            return cloneElement(
              node,
              { key: elementKey },
              textContent.replace(stringDate, localizedDate),
            );
          }
        }
      }
    }

    return cloneElement(node, { key: elementKey });
  });

  return (
    <div className={styles.wrapperMessageDescription}>
      { currentMessage && isMessageReady && renderedBody }
    </div>
  );
};

export default NotificationInfo;
